// pages/AdminPanel/AdminRefurbishedPhones.jsx
import React, { useEffect, useState } from 'react';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import { Plus, Edit, Trash2, Smartphone, X } from 'lucide-react';
import uploadImage from '../../helpers/uploadImage';
import { authFetch } from '../../helpers/authFetch';

function AdminRefurbishedPhones() {
  const { colors } = useTheme();
  
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  const [formData, setFormData] = useState({
    phoneName: '',
    phoneBrand: '',
    phoneModel: '',
    phoneImages: [],
    phoneCategory: 'Smartphone',
    storage: '',
    color: '',
    ram: '',
    processor: '',
    screenSize: '',
    batteryCapacity: '',
    condition: 'Good',
    conditionDetails: '',
    warrantyMonths: 0,
    originalPrice: '',
    phonePrice: '',
    phoneDiscount: '',
    inStock: true,
    quantityAvailable: 1,
    phoneDescription: ''
  });

  const fetchPhones = async () => {
    try {
      setLoading(true);
      const response = await authFetch(summaryApi.getRefurbishedPhones.url, {
        method: summaryApi.getRefurbishedPhones.method
        
      });

      const data = await response.json();

      if (data.success) {
        setPhones(data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch phones');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const uploadResult = await uploadImage(file);
      
      setFormData(prev => ({
        ...prev,
        phoneImages: [...prev.phoneImages, uploadResult.secure_url]
      }));
      
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      phoneImages: prev.phoneImages.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (formData.phoneImages.length === 0) {
    toast.error('Please upload at least one image');
    return;
  }

  setProcessing(true);
  try {
    // FIX: Properly construct the URL
    const url = editMode 
      ? `${summaryApi.updateRefurbishedPhone.url}/${formData._id}` // Just append the ID
      : summaryApi.addRefurbishedPhone.url;
      
    const method = editMode 
      ? summaryApi.updateRefurbishedPhone.method
      : summaryApi.addRefurbishedPhone.method;

    console.log('🔄 Making request to:', url); // Debug log

    const response = await authFetch(url, {
      method,
      
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });

    const data = await response.json();

    if (data.success) {
      toast.success(`Phone ${editMode ? 'updated' : 'added'} successfully`);
      setShowModal(false);
      resetForm();
      fetchPhones();
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    toast.error(`Failed to ${editMode ? 'update' : 'add'} phone`);
    console.error('Error:', error);
  } finally {
    setProcessing(false);
  }
};

const handleDelete = async (phoneId) => {
  if (!window.confirm('Are you sure you want to delete this phone?')) return;

  try {
    // FIX: Properly construct the URL
    const url = `${summaryApi.deleteRefurbishedPhone.url}/${phoneId}`;
    
    console.log('🗑️ Making delete request to:', url); // Debug log

    const response = await authFetch(url, {
      method: summaryApi.deleteRefurbishedPhone.method
      
    });

    const data = await response.json();

    if (data.success) {
      toast.success('Phone deleted successfully');
      fetchPhones();
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    toast.error('Failed to delete phone');
    console.error('Error:', error);
  }
};



  const openEditModal = (phone) => {
    setFormData(phone);
    setEditMode(true);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      phoneName: '',
      phoneBrand: '',
      phoneModel: '',
      phoneImages: [],
      phoneCategory: 'Smartphone',
      storage: '',
      color: '',
      ram: '',
      processor: '',
      screenSize: '',
      batteryCapacity: '',
      condition: 'Good',
      conditionDetails: '',
      warrantyMonths: 0,
      originalPrice: '',
      phonePrice: '',
      phoneDiscount: '',
      inStock: true,
      quantityAvailable: 1,
      phoneDescription: ''
    });
    setEditMode(false);
  };

  useEffect(() => {
    fetchPhones();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: colors.text }}>
            Refurbished Phones
          </h1>
          <p style={{ color: colors.textSecondary }}>
            Manage refurbished phone inventory
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-6 py-3 rounded-lg font-semibold text-white transition-all hover:scale-105 flex items-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
          }}
        >
          <Plus size={20} />
          Add Phone
        </button>
      </div>

      {/* Phones Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent" style={{ borderTopColor: colors.primary }} />
        </div>
      ) : phones.length === 0 ? (
        <div
          className="text-center py-16 rounded-xl"
          style={{ backgroundColor: colors.bgSecondary }}
        >
          <Smartphone size={48} className="mx-auto mb-4" style={{ color: colors.textSecondary, opacity: 0.3 }} />
          <p style={{ color: colors.textSecondary }}>No refurbished phones yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {phones.map((phone) => (
            <div
              key={phone._id}
              className="rounded-xl overflow-hidden transition-all hover:shadow-lg"
              style={{
                backgroundColor: colors.bgSecondary,
                border: `1px solid ${colors.border}`
              }}
            >
              <img
                src={phone.phoneImages[0]}
                alt={phone.phoneName}
                className="w-full h-48 object-cover"
              />
              
              <div className="p-4 space-y-3">
                <h3 className="font-bold text-lg" style={{ color: colors.text }}>
                  {phone.phoneName}
                </h3>

                <div className="flex items-center justify-between">
                  <span
                    className="px-3 py-1 rounded-full text-xs font-bold"
                    style={{
                      backgroundColor: phone.inStock ? '#10B98120' : '#EF444420',
                      color: phone.inStock ? '#10B981' : '#EF4444'
                    }}
                  >
                    {phone.inStock ? 'In Stock' : 'Out of Stock'}
                  </span>
                  <span className="text-sm" style={{ color: colors.textSecondary }}>
                    Qty: {phone.quantityAvailable}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold" style={{ color: colors.primary }}>
                    ₦{phone.phonePrice.toLocaleString()}
                  </span>
                  {phone.phoneDiscount && (
                    <span className="text-sm line-through" style={{ color: colors.textSecondary }}>
                      ₦{phone.phoneDiscount.toLocaleString()}
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(phone)}
                    className="flex-1 py-2 rounded-lg font-semibold transition-all hover:scale-105 flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: colors.primaryLight,
                      color: colors.primary
                    }}
                  >
                    <Edit size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(phone._id)}
                    className="flex-1 py-2 rounded-lg font-semibold transition-all hover:scale-105 flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: '#EF444420',
                      color: '#EF4444'
                    }}
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div
            className="rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`
            }}
          >
            {/* Header */}
            <div
              className="p-6 border-b flex items-center justify-between sticky top-0 z-10"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.bgSecondary
              }}
            >
              <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
                {editMode ? 'Edit' : 'Add'} Refurbished Phone
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-lg transition-all hover:scale-110"
                style={{
                  backgroundColor: colors.bg,
                  color: colors.primary
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Phone Name *
                  </label>
                  <input
                    type="text"
                    name="phoneName"
                    value={formData.phoneName}
                    onChange={handleChange}
                    required
                    placeholder="e.g., iPhone 12 Pro - Refurbished"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Brand *
                  </label>
                  <input
                    type="text"
                    name="phoneBrand"
                    value={formData.phoneBrand}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Apple"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Model *
                  </label>
                  <input
                    type="text"
                    name="phoneModel"
                    value={formData.phoneModel}
                    onChange={handleChange}
                    required
                    placeholder="e.g., iPhone 12 Pro"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Category
                  </label>
                  <select
                    name="phoneCategory"
                    value={formData.phoneCategory}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  >
                    <option value="Smartphone">Smartphone</option>
                    <option value="Tablet">Tablet</option>
                    <option value="Smartwatch">Smartwatch</option>
                    <option value="Laptop">Laptop</option>
                    <option value="Accessory">Accessory</option>
                  </select>
                </div>
              </div>

              {/* Specifications */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Storage
                  </label>
                  <input
                    type="text"
                    name="storage"
                    value={formData.storage}
                    onChange={handleChange}
                    placeholder="e.g., 128GB"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    RAM
                  </label>
                  <input
                    type="text"
                    name="ram"
                    value={formData.ram}
                    onChange={handleChange}
                    placeholder="e.g., 6GB"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Color
                  </label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder="e.g., Pacific Blue"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>
              </div>

              {/* Condition */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Condition *
                  </label>
                  <select
                    name="condition"
                    value={formData.condition}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  >
                    <option value="Like New">Like New</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Warranty (Months)
                  </label>
                  <input
                    type="number"
                    name="warrantyMonths"
                    value={formData.warrantyMonths}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Original Price (₦)
                  </label>
                  <input
                    type="number"
                    name="originalPrice"
                    value={formData.originalPrice}
                    onChange={handleChange}
                    placeholder="0"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Selling Price (₦) *
                  </label>
                  <input
                    type="number"
                    name="phonePrice"
                    value={formData.phonePrice}
                    onChange={handleChange}
                    required
                    placeholder="0"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Discount Price (₦)
                  </label>
                  <input
                    type="number"
                    name="phoneDiscount"
                    value={formData.phoneDiscount}
                    onChange={handleChange}
                    placeholder="0"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>
              </div>

              {/* Stock */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Quantity Available
                  </label>
                  <input
                    type="number"
                    name="quantityAvailable"
                    value={formData.quantityAvailable}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="inStock"
                      checked={formData.inStock}
                      onChange={handleChange}
                      className="w-5 h-5"
                    />
                    <span className="text-sm font-semibold" style={{ color: colors.text }}>
                      In Stock
                    </span>
                  </label>
                </div>
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-semibold mb-3" style={{ color: colors.text }}>
                  Phone Images *
                </label>
                <div className="grid grid-cols-4 gap-4">
                  {formData.phoneImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`Phone ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}

                  <label
                    className="h-32 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all hover:scale-105"
                    style={{ borderColor: colors.border }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      className="hidden"
                    />
                    {uploadingImage ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-transparent" style={{ borderTopColor: colors.primary }} />
                    ) : (
                      <>
                        <Plus size={32} style={{ color: colors.primary }} />
                        <span className="text-xs mt-2" style={{ color: colors.textSecondary }}>
                          Add Image
                        </span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold mb-3" style={{ color: colors.text }}>
                  Description *
                </label>
                <textarea
                  name="phoneDescription"
                  value={formData.phoneDescription}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="Describe the phone condition, features, etc..."
                  className="w-full px-4 py-3 rounded-lg outline-none resize-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={processing || uploadingImage}
                className="w-full py-4 rounded-lg font-bold text-white transition-all hover:scale-105 disabled:opacity-50"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                }}
              >
                {processing ? 'Processing...' : editMode ? 'Update Phone' : 'Add Phone'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminRefurbishedPhones;